import { useState, useCallback, useEffect, useRef } from "react";
import { GroupBy } from "app/data/helpers";
import { useAppDispatch, useAppSelector, useSelectSearch } from "hooks";
import {
  selectAccountGroups,
  selectDrawerSelectedAccountId,
  selectObjectsState,
  selectObjectTypesState,
  selectQueriedObjectsState,
  selectReportsState
} from "app/data/selectors";
import {
  getAllAccountGroupsThunk,
  getAllObjectsThunk,
  getInitialObjectsForDropdownListThunk,
  getObjectsByQueryThunk,
  getObjectTypesByAccountThunk,
  resetPreloadedObjects
} from "app/features";
import {
  IReportsFilterData,
  IReportsDateValues
} from "app/features/reports/reportsSlice";
import { DateTime } from "luxon";

const useReportCommonStates = () => {
  const dispatch = useAppDispatch();
  const isFetching = useRef(false);

  // Global State
  const drawerSelectedAccountId = useAppSelector(selectDrawerSelectedAccountId);

  const { objectTypes, loading: objectTypesLoading } = useAppSelector(
    selectObjectTypesState
  );

  const { objects, loading: objectsLoading } = useAppSelector(
    selectQueriedObjectsState
  );

  const { basicObjects, initialObjectsCount, preloadedObjects } =
    useAppSelector(selectObjectsState);

  const { accountGroups } = useAppSelector(selectAccountGroups);

  const {
    report,
    loading: reportsLoading,
    reportsFilterData,
    reportsHasExceededLimit,
    reportsDateValues
  } = useAppSelector(selectReportsState);

  // Local State
  const [filterOpen, setFilterOpen] = useState(false);

  const [dateFormat, setDateFormat] = useState<GroupBy>("day");

  // Initialize dateValues from reportsDateValues if available
  const [dateValues, setDateValues] = useState(() => {
    if (reportsDateValues?.startDate && reportsDateValues?.endDate) {
      return {
        startDate: reportsDateValues.startDate,
        endDate: reportsDateValues.endDate
      };
    }
    // Default to today in local timezone
    const today = DateTime.now();
    return {
      startDate: today.toJSDate(),
      endDate: today.toJSDate()
    };
  });

  const [localReport, setLocalReport] = useState<any>([]);

  // Initialize selectedLocalIds from reportsFilterData if available
  const [selectedLocalIds, setSelectedLocalIds] = useState(() => {
    const filterData = reportsFilterData as IReportsFilterData;
    if (filterData?.object) {
      return filterData.object;
    }
    return [];
  });

  const selectedLocalObjects = useSelectSearch(selectedLocalIds, objects);

  const handleOnChangeReportFilterForm = (val: any) => {
    setSelectedLocalIds(val);
  };

  const handleObjectsSearch = useCallback(
    (value: string) => {
      dispatch(
        getObjectsByQueryThunk({
          accountId: drawerSelectedAccountId || "",
          params: {
            itemsPerPage: 50,
            page: 1,
            where: [{ field: "name", value, type: 0 }]
          },
          minimal: true
        })
      );
    },
    [drawerSelectedAccountId]
  );

  const preloadInitialObjects = async () => {
    isFetching.current = true;
    const { type, payload } = await dispatch(
      getInitialObjectsForDropdownListThunk(drawerSelectedAccountId || "")
    );

    if (type === "objects/getInitialObjectsForDropdownListThunk/fulfilled") {
      if (payload < 26) {
        await dispatch(getAllObjectsThunk(drawerSelectedAccountId || ""));
      } else {
        dispatch(resetPreloadedObjects());
      }
    }
    isFetching.current = false;
  };

  useEffect(() => {
    if (isFetching.current === false) {
      preloadInitialObjects();
      dispatch(getAllAccountGroupsThunk(drawerSelectedAccountId || ""));
      dispatch(getObjectTypesByAccountThunk(drawerSelectedAccountId || ""));
    }
  }, [drawerSelectedAccountId]);

  // Update local state when reportsDateValues changes
  useEffect(() => {
    if (reportsDateValues?.startDate && reportsDateValues?.endDate) {
      // Create dates in UTC to avoid timezone issues
      const startDate = new Date(reportsDateValues.startDate);
      const endDate = new Date(reportsDateValues.endDate);

      // Adjust to local timezone
      setDateValues({
        startDate,
        endDate
      });
    }
  }, [reportsDateValues]);

  // Update local state when reportsFilterData changes
  useEffect(() => {
    const filterData = reportsFilterData as IReportsFilterData;
    if (filterData?.object) {
      setSelectedLocalIds(filterData.object);
    }
  }, [reportsFilterData]);

  return {
    drawerSelectedAccountId,
    objectTypes,
    objectTypesLoading,
    objects,
    objectsLoading,
    accountGroups,
    report,
    reportsLoading,
    filterOpen,
    setFilterOpen,
    dateFormat,
    setDateFormat,
    dateValues,
    setDateValues,
    localReport,
    setLocalReport,
    handleObjectsSearch,
    selectedLocalObjects,
    selectedLocalIds,
    handleOnChangeReportFilterForm,
    basicObjects,
    initialObjectsCount,
    reportsFilterData,
    reportsHasExceededLimit,
    preloadedObjects
  };
};

export default useReportCommonStates;
